/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { ReactNode } from "react";
import { Box, Divider, Paper, SxProps, Theme, Typography } from "@mui/material";
import { mergeSx } from "@/src/utils/mergeSx";
import { TypographyProps } from "@mui/material/Typography";

const VARIANT_CONFIG = {
  clean: {
    showDivider: false,
    typographyProps: { variant: "cardTitle", component: "h2", sx: { marginBottom: "1rem" } },
  },
  divider: {
    showDivider: true,
    typographyProps: { variant: "cardTitle", component: "h2", sx: { marginBottom: 0 } },
  },
  hintbox: {
    showDivider: true,
    typographyProps: { variant: "h5", component: "h2", sx: { marginBottom: 0 } },
  },
} satisfies Record<string, { showDivider: boolean; typographyProps: TypographyProps }>;

export type ProductBoxVariant = keyof typeof VARIANT_CONFIG;

export type ProductBoxProps = {
  headline?: ReactNode;
  children: ReactNode;
  variant?: ProductBoxVariant;
  sx?: SxProps<Theme>;
};

export default function ProductBox({ headline, children, variant = "clean", sx }: ProductBoxProps) {
  const config = VARIANT_CONFIG[variant];

  return (
    <Paper elevation={4} sx={mergeSx({ padding: { xs: "1rem 1rem 1.5rem", md: "1.5rem 1.5rem 2rem" }, marginBottom: "1rem" }, sx)}>
      {!!headline && <Typography {...config.typographyProps}>{headline}</Typography>}

      {config.showDivider && (
        <Divider
          sx={(theme) => ({
            color: theme.palette.divider,
            marginTop: "0.5rem",
            marginBottom: "1rem",
          })}
        />
      )}

      <Box>{children}</Box>
    </Paper>
  );
}
